'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { isEmpty } from 'underscore';
import styled, { css } from 'styled-components';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import FormFieldContainer from 'FormsUI/components/editor/form/formFields/FormFieldContainer';
import FormFieldDropTarget from 'FormsUI/components/editor/form/dropZone/FormFieldDropTarget';
import FormFieldGroupDropTarget from 'FormsUI/components/editor/form/dropZone/FormFieldGroupDropTarget';
import DraggableRichText from 'FormsUI/components/editor/form/formFields/DraggableRichText';
import { getPropertyObjectTypeId } from 'FormsUI/utils/PropertiesUtils';
import { CommentableArea } from 'collaboration-sidebar';
import { SpecialFieldTypes, RichTextTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import imageUrl from 'bender-url!FormsUI/img/editor/linked-rows-icon.svg';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCommentableArea = styled(CommentableArea).withConfig({
  displayName: "FormFieldGroup__StyledCommentableArea"
})(["width:100%;"]);
const DropTargetContainer = styled.div.withConfig({
  displayName: "FormFieldGroup__DropTargetContainer"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return css(["width:", ";z-index:1;transform:translateY(2.8em) translateX(8px);&:first-of-type{transform:translateX(19px) translateY(2.8em);}&:last-of-type{transform:translateX(-2px) translateY(2.8em);}"], theme.space['0']);
});
const HorizontalDroppableView = ({
  children
}) => /*#__PURE__*/_jsx(View, {
  "data-test-id": "form-field-group-drop-target-container",
  inlineSize: "100%",
  displayBlock: "16px",
  paddingBlockStart: "16px",
  paddingInlineStart: "45px",
  paddingInlineEnd: "18px",
  children: children
});
export default class FormFieldGroup extends PureComponent {
  renderFormField(formFields, formFieldGroupIndex, formField, formFieldIndex, key) {
    const {
      handleFormFieldSelect,
      selectedFormItemReference,
      removeFormField,
      removeDependentField,
      richText,
      showHighlightAnimation,
      isQueued,
      formType
    } = this.props;
    return /*#__PURE__*/_jsx(Box, {
      "data-test-id": "form-field-container__flex-child",
      grow: 1,
      basis: 0,
      children: /*#__PURE__*/_jsx(CommentableArea, {
        moduleId: getPropertyObjectTypeId(formField),
        verticalPositioning: true,
        description: {
          translationKey: formField.label ? 'formsFieldWithLabel' : 'formsField',
          options: {
            fieldLabel: formField.label
          }
        },
        children: /*#__PURE__*/_jsx(FormFieldContainer, {
          numberFormFieldsWithinGroup: formFields.length,
          formFieldGroupIndex: formFieldGroupIndex,
          formFieldIndex: formFieldIndex,
          formField: formField,
          handleInputSelect: handleFormFieldSelect,
          handleInputDelete: removeFormField,
          selectedFieldName: selectedFormItemReference,
          handleDrag: this.props.updateDraggedItem,
          removeDependentField: removeDependentField,
          groupHasRichText: !isEmpty(richText.content),
          showHighlightAnimation: showHighlightAnimation,
          isQueuedField: isQueued,
          isFormLabelPropertyEnabled: this.props.isFormLabelPropertyEnabled,
          formType: formType
        })
      })
    }, key);
  }
  renderFormFieldDropTarget(formField, index, key) {
    const {
      formFieldGroupIndex,
      addFormField,
      moveFormFieldIntoExistingFormFieldGroup,
      formFields,
      isQueued
    } = this.props;
    return /*#__PURE__*/_jsx(DropTargetContainer, {
      "data-test-id": "form-field-drop-target",
      children: /*#__PURE__*/_jsx(Box, {
        grow: 0,
        children: /*#__PURE__*/_jsx(FormFieldDropTarget, {
          formFieldGroupIndex: formFieldGroupIndex,
          formFieldIndex: index,
          addFormField: addFormField,
          moveFormFieldIntoExistingFormFieldGroup: moveFormFieldIntoExistingFormFieldGroup,
          numberFormFieldsWithinGroup: formFields.length,
          isQueued: isQueued
        })
      })
    }, key);
  }
  render() {
    const {
      handleRichTextSelect,
      addFormFieldGroup,
      moveFormFieldIntoNewFormFieldGroup,
      addRichText,
      removeRichText,
      reorderRichText,
      formFields,
      richText,
      formFieldGroupIndex,
      formFieldGroupsLength,
      selectedFormItemReference,
      updateDraggedItem,
      isQueued,
      isRichTextLinked,
      isPageBreak
    } = this.props;
    const formFieldComponents = formFields.reduce((components, formField, index, {
      length
    }) => {
      components.push(this.renderFormFieldDropTarget(formField, index, components.length));
      components.push(this.renderFormField(formFields, formFieldGroupIndex, formField, index, JSON.stringify(formField)));
      if (index === length - 1) {
        components.push(this.renderFormFieldDropTarget(formField, index + 1, components.length));
      }
      return components;
    }, []);
    const maybeLastFormFieldGroupTarget = formFieldGroupsLength - 1 === formFieldGroupIndex ? /*#__PURE__*/_jsx(HorizontalDroppableView, {
      children: /*#__PURE__*/_jsx(FormFieldGroupDropTarget, {
        formFieldGroupIndex: formFieldGroupsLength,
        addFormFieldGroup: addFormFieldGroup,
        moveFormFieldIntoNewFormFieldGroup: moveFormFieldIntoNewFormFieldGroup,
        addRichText: addRichText,
        reorderRichText: reorderRichText,
        isLastRow: true,
        hasRichText: false,
        isQueued: isQueued
      })
    }) : null;
    const maybeRichText = richText && (!isEmpty(richText.content) || !isRichTextLinked) && !isPageBreak ? /*#__PURE__*/_jsx(StyledCommentableArea, {
      moduleId: `richtext-area-${formFieldGroupIndex}`,
      children: /*#__PURE__*/_jsxs(View, {
        inlineSize: "100%",
        children: [/*#__PURE__*/_jsx(DraggableRichText, {
          selectedFormItemType: richText.type === RichTextTypes.IMAGE ? SpecialFieldTypes.IMAGE : SpecialFieldTypes.RICH_TEXT,
          selectedFormItemReference: selectedFormItemReference,
          formFieldGroupIndex: formFieldGroupIndex,
          richText: richText,
          handleRichTextSelect: handleRichTextSelect,
          handleRichTextDelete: removeRichText,
          handleDrag: updateDraggedItem,
          isQueued: isQueued
        }), isRichTextLinked && /*#__PURE__*/_jsx("div", {
          "data-test-id": "rich-text-link",
          children: /*#__PURE__*/_jsx("img", {
            src: imageUrl
          })
        })]
      })
    }) : null;
    return /*#__PURE__*/_jsx(View, {
      inlineSize: "95%" // to pull it away from collab sidebar
      ,
      blockSize: "100%",
      "data-test-id": "form-field-group-container",
      marginBlock: "300",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "start",
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(HorizontalDroppableView, {
          children: /*#__PURE__*/_jsx(FormFieldGroupDropTarget, {
            formFieldGroupIndex: formFieldGroupIndex,
            addFormFieldGroup: addFormFieldGroup,
            moveFormFieldIntoNewFormFieldGroup: moveFormFieldIntoNewFormFieldGroup,
            addRichText: addRichText,
            reorderRichText: reorderRichText,
            hasRichText: !richText || !isEmpty(richText.content),
            isLastRow: false,
            isQueued: isQueued
          })
        }), maybeRichText, /*#__PURE__*/_jsx(View, {
          inlineSize: "100%",
          blockSize: "100%",
          children: /*#__PURE__*/_jsx(Flex, {
            flexDirection: "row",
            alignItems: "start",
            children: formFieldComponents
          })
        }), maybeLastFormFieldGroupTarget]
      })
    });
  }
}
FormFieldGroup.propTypes = {
  addFormFieldGroup: PropTypes.func.isRequired,
  addFormField: PropTypes.func.isRequired,
  removeFormField: PropTypes.func.isRequired,
  handleRichTextSelect: PropTypes.func.isRequired,
  addRichText: PropTypes.func.isRequired,
  removeRichText: PropTypes.func.isRequired,
  reorderRichText: PropTypes.func.isRequired,
  moveFormFieldIntoNewFormFieldGroup: PropTypes.func.isRequired,
  moveFormFieldIntoExistingFormFieldGroup: PropTypes.func.isRequired,
  handleFormFieldSelect: PropTypes.func.isRequired,
  removeDependentField: PropTypes.func.isRequired,
  updateDraggedItem: PropTypes.func.isRequired,
  selectedFormItemReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formFields: PropTypes.array.isRequired,
  richText: PropTypes.object,
  formFieldGroupIndex: PropTypes.number.isRequired,
  formFieldGroupsLength: PropTypes.number.isRequired,
  isQueued: PropTypes.bool.isRequired,
  showHighlightAnimation: PropTypes.bool.isRequired,
  formType: PropTypes.string.isRequired,
  isFormLabelPropertyEnabled: PropTypes.bool.isRequired,
  isRichTextLinked: PropTypes.bool,
  isPageBreak: PropTypes.bool,
  stepNumber: PropTypes.number
};